'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Star, MapPin, Heart, Share2, Crown, Tag, Loader2, ChevronLeft, ToggleLeft, ToggleRight,  } from 'lucide-react';
import { Offer } from '@/types';
import SafeImage from '@/components/SafeImage';
import BottomNav from '@/components/BottomNav';
import { toast } from 'sonner';
import WinewoodPlusModal from '@/components/WinewoodPlusModal';
import InquiryModal from '@/app/offer-detail-page/components/InquiryModal';
import { useAuth } from '@/contexts/AuthContext';

interface SpecialsPageContentProps {
  offers: Offer[];
}

export default function SpecialsPageContent({ offers }: SpecialsPageContentProps) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(
    new Set(offers.filter((o) => o.is_favorited).map((o) => o.id))
  );
  const [nearbyEnabled, setNearbyEnabled] = useState(false);
  const [locating, setLocating] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [plusModalHint, setPlusModalHint] = useState<string | undefined>(undefined);
  const [inquiryOffer, setInquiryOffer] = useState<Offer | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        toast.success('Aus Favoriten entfernt', { icon: '💔', duration: 1800 });
      } else {
        next.add(id);
        toast.success('Zu Favoriten hinzugefügt', { icon: '❤️', duration: 1800 });
      }
      return next;
    });
  };

  const handleShare = (offer: Offer, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({ title: offer.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!', { icon: '🔗', duration: 1800 });
    }
  };

  const handleNearbyToggle = () => {
    if (nearbyEnabled) {
      setNearbyEnabled(false);
      setUserLocation(null);
      return;
    }
    setLocating(true);
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocating(false);
          setNearbyEnabled(true);
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          toast.success('Standort gefunden!', {
            description: 'Zeige Angebote in deiner Nähe.',
            icon: '📍',
          });
        },
        () => {
          setLocating(false);
          toast.error('Standortzugriff verweigert', {
            description: 'Bitte erlaube Standortzugriff in deinen Einstellungen.',
          });
        },
        { timeout: 8000 }
      );
    } else {
      setLocating(false);
      toast.error('Geolocation nicht verfügbar in diesem Browser.');
    }
  };

  // Sort by distance if nearby is enabled
  const displayedOffers = nearbyEnabled
    ? [...offers].sort((a, b) => (a.distance_km ?? 999) - (b.distance_km ?? 999))
    : offers;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--background)' }}>
      {/* ── HEADER ── */}
      <div
        className="sticky top-0 z-40 px-4"
        style={{
          background: 'rgba(27,29,35,0.96)',
          borderBottom: '1px solid var(--border)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <div className="flex items-center justify-between max-w-mobile mx-auto">
          <Link
            href="/"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            aria-label="Zurück zur Entdecken-Seite"
          >
            <ChevronLeft size={18} color="var(--card-foreground)" />
          </Link>

          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Adventure"
            width={110}
            height={44}
            className="object-contain"
            priority
          />

          {/* Nearby toggle */}
          <button
            onClick={handleNearbyToggle}
            disabled={locating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-semibold transition-all duration-150 active:scale-95 disabled:opacity-60"
            style={{
              background: nearbyEnabled ? 'rgba(124,179,66,0.18)' : 'var(--muted)',
              color: nearbyEnabled ? 'var(--primary)' : 'var(--muted-foreground)',
              border: nearbyEnabled ? '1px solid rgba(124,179,66,0.4)' : '1px solid var(--border)',
            }}
            aria-label={nearbyEnabled ? 'Nähe-Filter deaktivieren' : 'Nähe-Filter aktivieren'}
          >
            {locating ? (
              <Loader2 size={13} className="animate-spin" />
            ) : nearbyEnabled ? (
              <ToggleRight size={15} />
            ) : (
              <ToggleLeft size={15} />
            )}
            {locating ? 'Suche...' : 'In der Nähe'}
          </button>
        </div>
      </div>

      {/* ── HERO BANNER ── */}
      <div className="px-4 pt-5 pb-4 max-w-mobile mx-auto">
        <div
          className="relative rounded-app overflow-hidden"
          style={{
            height: '140px',
            border: '1.5px solid rgba(124,179,66,0.45)',
            background: 'linear-gradient(135deg, rgba(40,80,10,0.9) 0%, rgba(20,50,5,0.95) 100%)',
          }}
        >
          {/* Decorative glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at 20% 50%, rgba(124,179,66,0.18) 0%, transparent 65%)',
            }}
          />

          <div className="relative z-10 flex flex-col justify-center h-full px-5">
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(124,179,66,0.22)',
                  border: '1.5px solid rgba(124,179,66,0.6)',
                }}
              >
                <Star size={17} color="#7CB342" fill="#7CB342" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">Spezial Angebote</h1>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  Exklusive Deals nur für dich
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="text-[11px] font-bold px-2.5 py-1 rounded-pill"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {displayedOffers.length} Angebote
              </span>
              {nearbyEnabled && (
                <span
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-pill"
                  style={{
                    background: 'rgba(124,179,66,0.2)',
                    color: 'var(--primary)',
                    border: '1px solid rgba(124,179,66,0.35)',
                  }}
                >
                  📍 Nach Nähe sortiert
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── OFFER LIST ── */}
      <div className="px-4 max-w-mobile mx-auto">
        {displayedOffers.length === 0 ? (
          <div className="text-center py-16">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ background: 'var(--muted)' }}
            >
              <Star size={24} color="var(--muted-foreground)" />
            </div>
            <p className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>
              Keine Spezial-Angebote
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Schau bald wieder vorbei!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {displayedOffers.map((offer) => {
              const isFav = favorites.has(offer.id);
              const isFree = offer.price === 0;
              const hasDiscount = offer.discount_amount > 0;
              const discountedPrice = offer.price - offer.discount_amount;

              return (
                <Link key={`special-${offer.id}`} href={`/offer-detail-page?id=${offer.id}`}>
                  <div
                    className="rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.98]"
                    style={{
                      background: 'var(--card)',
                      border: '1.5px solid rgba(124,179,66,0.4)',
                      boxShadow: '0 0 18px rgba(124,179,66,0.15), 0 4px 16px rgba(0,0,0,0.4)',
                    }}
                  >
                    {/* Image */}
                    <div className="relative" style={{ height: '210px' }}>
                      <SafeImage
                        src={offer.image_url}
                        alt={`${offer.title} in ${offer.city} — Spezial Angebot`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 430px) 100vw, 430px"
                      />
                      {/* Gradient overlay */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)',
                        }}
                      />
                      {/* Green tint at top */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(to bottom, rgba(40,80,10,0.25) 0%, transparent 40%)',
                        }}
                      />

                      {/* Fav + Share — top left */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <button
                          onClick={(e) => toggleFavorite(offer.id, e)}
                          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                          style={{
                            background: 'rgba(0,0,0,0.5)',
                            backdropFilter: 'blur(8px)',
                          }}
                          aria-label={isFav ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                        >
                          <Heart
                            size={17}
                            color={isFav ? '#ef4444' : 'white'}
                            fill={isFav ? '#ef4444' : 'none'}
                          />
                        </button>
                        <button
                          onClick={(e) => handleShare(offer, e)}
                          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                          style={{
                            background: 'rgba(0,0,0,0.5)',
                            backdropFilter: 'blur(8px)',
                          }}
                          aria-label={`${offer.title} teilen`}
                        >
                          <Share2 size={16} color="white" />
                        </button>
                      </div>

                      {/* Distance badge — top right */}
                      {offer.distance_km !== undefined && (
                        <div
                          className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-pill"
                          style={{
                            background: nearbyEnabled
                              ? 'rgba(124,179,66,0.85)'
                              : 'rgba(0,0,0,0.58)',
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <MapPin
                            size={11}
                            color={nearbyEnabled ? '#fff' : 'var(--primary)'}
                          />
                          <span
                            className="text-[11px] font-semibold text-white"
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {offer.distance_km < 1
                              ? `${(offer.distance_km * 1000).toFixed(0)} m`
                              : `${offer.distance_km.toFixed(1)} km`}
                          </span>
                        </div>
                      )}

                      {/* Spezial badge — bottom left */}
                      <div className="absolute bottom-3 left-3">
                        <div
                          className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white"
                          style={{ background: 'var(--primary)' }}
                        >
                          <Star size={11} fill="white" />
                          Spezial
                        </div>
                      </div>
                    </div>

                    {/* Card body */}
                    <div
                      className="p-3.5"
                      style={{
                        borderTop: '1px solid rgba(124,179,66,0.2)',
                        background: 'linear-gradient(to bottom, rgba(124,179,66,0.04) 0%, transparent 60%)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--card-foreground)' }}>
                            {offer.title}
                          </h3>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin size={11} color="var(--muted-foreground)" />
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                              {offer.city}
                            </span>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right flex-shrink-0">
                          {isFree ? (
                            <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>
                              Kostenlos
                            </span>
                          ) : hasDiscount && offer.requires_plus ? (
                            <div className="flex flex-col items-end">
                              <span
                                className="text-xs line-through"
                                style={{
                                  color: 'var(--muted-foreground)',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {offer.price.toFixed(2).replace('.', ',')} €
                              </span>
                              <div className="flex items-center gap-1">
                                <Crown size={11} color="var(--gold)" />
                                <span
                                  className="text-sm font-bold"
                                  style={{ color: 'var(--gold)', fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {discountedPrice.toFixed(2).replace('.', ',')} €
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span
                              className="text-sm font-bold"
                              style={{
                                color: 'var(--card-foreground)',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {offer.price.toFixed(2).replace('.', ',')} €
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Discount hint */}
                      {offer.discount_text && (
                        <div className="mt-2.5 flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              if (offer.requires_plus) {
                                e.preventDefault();
                                e.stopPropagation();
                                setPlusModalHint(`Schalte den WINEWOOD+ Rabatt für "${offer.title}" frei.`);
                                setShowPlusModal(true);
                              }
                            }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-pill transition-all duration-150"
                            style={{
                              background: offer.requires_plus
                                ? 'rgba(234,179,8,0.1)'
                                : 'rgba(124,179,66,0.12)',
                              border: `1px solid ${
                                offer.requires_plus
                                  ? 'rgba(234,179,8,0.3)'
                                  : 'rgba(124,179,66,0.35)'
                              }`,
                              cursor: offer.requires_plus ? 'pointer' : 'default',
                            }}
                          >
                            {offer.requires_plus ? (
                              <Crown size={11} color="var(--gold)" />
                            ) : (
                              <Tag size={11} color="var(--primary)" />
                            )}
                            <span
                              className="text-[11px] font-semibold"
                              style={{
                                color: offer.requires_plus ? 'var(--gold)' : 'var(--primary)',
                              }}
                            >
                              {offer.discount_text}
                            </span>
                          </button>
                          {offer.requires_plus && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPlusModalHint(`Schalte den WINEWOOD+ Rabatt für "${offer.title}" frei.`);
                                setShowPlusModal(true);
                              }}
                              className="text-[10px] underline"
                              style={{ color: 'var(--muted-foreground)' }}
                            >
                              nur WINEWOOD+
                            </button>
                          )}
                        </div>
                      )}

                      {/* Category pills */}
                      {offer.categories.length > 0 && (
                        <div className="flex gap-1.5 mt-2.5 flex-wrap">
                          {offer.categories.slice(0, 2).map((cat) => (
                            <span
                              key={`scat-${offer.id}-${cat.id}`}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-pill"
                              style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                            >
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <WinewoodPlusModal
        isOpen={showPlusModal}
        onClose={() => setShowPlusModal(false)}
        contextHint={plusModalHint}
      />

      <InquiryModal
        isOpen={showInquiryModal}
        onClose={() => { setShowInquiryModal(false); setInquiryOffer(null); }}
        onSent={() => { setShowInquiryModal(false); setInquiryOffer(null); }}
        offer={inquiryOffer ?? offers[0]}
        isLoggedIn={!!user}
        userName={user?.email?.split('@')[0] || ''}
        userEmail={user?.email}
      />

      <BottomNav />
    </div>
  );
}
